import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, Empty } from "@/components/common";
import { usePortal } from "@/components/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { useApi, act } from "@/lib/useApi";
import { api, downloadBlob } from "@/lib/api";
import { dt, dtTime } from "@/lib/fmt";

const Row = ({ k, v }) => <div className="flex justify-between py-2 border-t border-slate-100 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-right">{v || "—"}</span></div>;

export default function PortalProfile() {
  const { sid } = usePortal();
  const { user } = useAuth();
  const [o, reloadO] = useApi(`/portal/students/${sid}`, [sid]);
  const [tpls] = useApi("/contracts/templates");
  const [acc, reloadAcc] = useApi(`/contracts/acceptances?student_id=${sid}`, [sid]);
  const [files] = useApi(`/portal/students/${sid}/files`, [sid]);
  const [read, setRead] = useState(null);
  const [agree, setAgree] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  if (!o) return <Empty text="Carregando…" />;
  const s = o.student;
  const minorStudent = s.age < 18 && user.role === "aluno";

  const accept = async () => {
    const r = await act(() => api.post("/contracts/accept", { student_id: sid, template_id: read.id }), "Aceite registrado");
    if (r) { setRead(null); setAgree(false); reloadAcc(); reloadO(); }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-extrabold uppercase">Perfil</h1>
      <div className="panel p-5" data-testid="portal-profile-data">
        <div className="font-display text-lg font-bold uppercase mb-1">Meus dados</div>
        <Row k="Nome" v={s.full_name} /><Row k="CPF" v={s.cpf} /><Row k="Nascimento" v={dt(s.birth_date)} /><Row k="Telefone" v={s.phone} /><Row k="E-mail" v={s.email} />
        <Row k="Endereço" v={s.address} />{s.guardian_name && <Row k="Responsável" v={`${s.guardian_name} · ${s.guardian_phone}`} />}<Row k="Matrícula" v={dt(s.enrollment_date)} />
        <p className="text-xs text-slate-500 mt-2">Para corrigir seus dados, fale com a recepção. Seus dados são tratados conforme a LGPD.</p>
      </div>
      <div className="panel p-5" data-testid="portal-contracts">
        <div className="font-display text-lg font-bold uppercase mb-2">Contratos e termos</div>
        {(tpls || []).filter((t) => !acc?.some((a) => a.template_id === t.id)).map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-lg bg-amber-50 p-3 mb-2 text-sm">
            <span><b>{t.title}</b> v{t.version}</span>
            <Button size="sm" onClick={() => setRead(t)} className="bg-red-600 hover:bg-red-700" data-testid={`portal-read-${t.id}`}>Ler e aceitar</Button></div>))}
        {(acc || []).map((a) => <div key={a.id} className="text-sm py-2 border-t border-slate-100"><b>{a.title}</b> v{a.version}<div className="text-xs text-slate-500">aceito por {a.accepted_by_name} em {dtTime(a.accepted_at)} ({a.method})</div></div>)}
      </div>
      <div className="panel p-5" data-testid="portal-files">
        <div className="font-display text-lg font-bold uppercase mb-2">Documentos</div>
        {!files?.length ? <Empty text="Nenhum documento disponível." /> : files.map((f) => (
          <div key={f.id} className="flex justify-between items-center py-2 border-t border-slate-100 text-sm"><span>{f.original_filename}<div className="text-xs text-slate-500">{f.category}</div></span>
            <Button size="sm" variant="outline" onClick={() => downloadBlob(`/files/${f.id}`, f.original_filename, true)}>Abrir</Button></div>))}
      </div>
      <div className="panel p-5 space-y-3" data-testid="portal-password">
        <div className="font-display text-lg font-bold uppercase">Alterar senha</div>
        <Field label="Senha atual"><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current-input" /></Field>
        <Field label="Nova senha (mín. 8)"><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new-input" /></Field>
        <Button variant="outline" onClick={async () => { if (await act(() => api.post("/auth/change-password", pw), "Senha alterada")) setPw({ current_password: "", new_password: "" }); }} data-testid="pw-save-btn">Salvar nova senha</Button>
      </div>
      {read && <Dialog open onOpenChange={() => setRead(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="portal-contract-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{read.title} · v{read.version}</DialogTitle></DialogHeader>
          <pre className="whitespace-pre-wrap text-sm font-sans text-slate-700 bg-slate-50 rounded-lg p-4 max-h-80 overflow-y-auto">{read.content}</pre>
          {minorStudent ? <div className="text-sm text-amber-700 bg-amber-50 rounded p-3">Como você é menor de idade, o aceite deve ser feito pelo seu responsável legal.</div> : <>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="portal-agree-checkbox" />
              Li e aceito integralmente este documento{user.role === "responsavel" && `, como responsável legal por ${s.full_name}`}. Registraremos data, hora, IP e a versão aceita.</label>
            <Button disabled={!agree} onClick={accept} className="bg-red-600 hover:bg-red-700" data-testid="portal-accept-btn">Aceitar eletronicamente</Button></>}
        </DialogContent>
      </Dialog>}
    </div>
  );
}
